import type React from 'react';
import { Anchor } from '@zillow/constellation-10';

/**
 * Represents a parsed chunk of text.
 */
type ParsedChunk = { type: 'text'; content: string } | { type: 'url'; content: string; href: string };

/**
 * URL detection regex (global, case-insensitive)
 *
 * What it matches:
 * - (?:(?:https?:\/\/)|www\.) - URLs starting with http://, https://, or www.
 * - [^\s<>()[\]{}"']+ - One or more characters that are NOT whitespace or common wrapping symbols
 * - [\w/] - Must end with a word character (a-z, A-Z, 0-9, _) or forward slash
 *
 * Examples of what it matches:
 * https://www.zillow.com/apartments/seattle-wa/artix/CjjqKc/
 * http://zillow.com/path?query=value
 * www.hotpads.com
 *
 * Examples of what it rejects:
 * zillow.com (no protocol or www)
 * https://zillow.com. (ends with punctuation)
 * www.zillow.com, (ends with comma)
 */
const URL_REGEX = /(?:(?:https?:\/\/)|www\.)[^\s<>()[\]{}"']+[\w/]/gi;

/**
 * Fast URL presence test regex (non-global, case-insensitive)
 *
 * Same pattern as URL_REGEX but without the global flag for performance.
 * Used as a quick check to determine if text contains any URL-like patterns
 * before running the more expensive parsing logic.
 *
 * This optimization avoids unnecessary processing for messages with no URLs.
 */
const URL_TEST_REGEX = /(?:(?:https?:\/\/)|www\.)[^\s<>()[\]{}"']+[\w/]/i;

/**
 * Parses raw text and extracts URLs and plain text into structured chunks.
 */
function parseTextToChunks(text: string): Array<ParsedChunk> {
  const chunks: Array<ParsedChunk> = [];
  let lastIndex = 0;

  if (!text) return [];

  for (const match of text.matchAll(URL_REGEX)) {
    const url = match[0];
    const start = match.index!;
    const end = start + url.length;

    // Check for malformed protocol directly preceding the match
    const preceding = text.slice(Math.max(0, start - 8), start);
    if (preceding.endsWith('https//') || preceding.endsWith('http//')) {
      continue; // skip this match
    }

    // Add any preceding plain text
    if (start > lastIndex) {
      chunks.push({ type: 'text', content: text.slice(lastIndex, start) });
    }

    const href = normalizeUrl(url);
    if (href) {
      chunks.push({ type: 'url', content: url, href });
    } else {
      chunks.push({ type: 'text', content: url });
    }

    lastIndex = end;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    chunks.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return chunks;
}

/**
 * Normalizes and validates a URL string.
 * - Adds https:// if missing
 * - Rejects malformed protocol
 * - Rejects numeric/IP/localhost hostnames
 * - Lowercases path
 * - Removes trailing slash (unless root)
 */
function normalizeUrl(rawUrl: string): string | null {
  let url = rawUrl.trim();

  // Reject malformed protocols explicitly
  if (url.startsWith('https//') || url.startsWith('http//')) {
    return null;
  }

  // Add https:// if no protocol
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`;
  }

  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname;

    // Reject IPs and localhost
    const isIP = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
    const isLocalhost = hostname === 'localhost';
    if (!hostname.includes('.') || isIP || isLocalhost) {
      return null;
    }

    // Remove trailing slash (unless root)
    if (parsed.pathname !== '/' && parsed.pathname.endsWith('/')) {
      parsed.pathname = parsed.pathname.slice(0, -1);
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Renders parsed chunks into React nodes with Anchor tags for URLs.
 * Uses onImpact prop for links that need high contrast on dark backgrounds.
 */
function renderChunks(chunks: Array<ParsedChunk>, onImpact?: boolean): Array<React.ReactNode> {
  let keyIndex = 0;
  return chunks.map((chunk) => {
    if (chunk.type === 'url') {
      return (
        <Anchor
          key={`link-${keyIndex++}`}
          href={chunk.href}
          target="_blank"
          rel="noopener noreferrer"
          onImpact={onImpact}
        >
          {chunk.content}
        </Anchor>
      );
    }
    return chunk.content;
  });
}

/**
 * Converts a string with URLs into an array of React nodes with clickable links.
 * @param text - The text to parse for URLs
 * @param onImpact - Whether to use high contrast styling for links on dark backgrounds
 */
export function parseTextWithLinks(text: string, onImpact?: boolean): Array<React.ReactNode> {
  if (!text) return [''];

  // Quick check to skip parsing if there's no URL-like content
  if (!URL_TEST_REGEX.test(text)) {
    return [text];
  }

  const chunks = parseTextToChunks(text);
  return chunks.length > 0 ? renderChunks(chunks, onImpact) : [text];
}
